import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, Menu, X } from "lucide-react";
import { COMPANY, PRIMARY_TEL, LOGO_EMBLEM } from "@/data";

const LINKS = [
  { label: "Services", href: "#services" },
  { label: "Fleet", href: "#fleet" },
  { label: "Experience", href: "#experience" },
  { label: "FAQ", href: "#faq" },
  { label: "Contact", href: "#contact" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
      data-testid="navbar"
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-500 ${
        scrolled ? "bg-black/70 backdrop-blur-2xl border-b border-white/10" : "bg-transparent"
      }`}
    >
      <nav className="mx-auto flex max-w-[1400px] items-center justify-between px-6 py-5 lg:px-10">
        <button onClick={() => go("#top")} data-testid="logo" className="flex items-center gap-3">
          <img src={LOGO_EMBLEM} alt="Admiral Limousine emblem" className="h-11 w-11 object-contain md:h-12 md:w-12" />
          <span className="flex flex-col leading-none">
            <span className="font-serif text-xl tracking-[0.35em] text-white md:text-2xl">ADMIRAL</span>
            <span className="mt-1 text-[9px] uppercase tracking-[0.4em] text-primary">Limousine Service</span>
          </span>
        </button>

        <div className="hidden items-center gap-9 lg:flex">
          {LINKS.map((l) => (
            <button
              key={l.href}
              onClick={() => go(l.href)}
              data-testid={`nav-${l.label.toLowerCase()}`}
              className="text-xs uppercase tracking-[0.15em] text-zinc-300 transition-colors duration-300 hover:text-primary"
            >
              {l.label}
            </button>
          ))}
        </div>

        <div className="hidden items-center gap-5 lg:flex">
          <a href={`tel:${PRIMARY_TEL}`} data-testid="nav-call" className="flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-white transition-colors hover:text-primary">
            <Phone className="h-3.5 w-3.5 text-primary" /> {COMPANY.phones[0].number}
          </a>
          <button
            onClick={() => go("#book")}
            data-testid="nav-book-ride"
            className="bg-primary px-6 py-3 text-xs font-medium uppercase tracking-[0.2em] text-primary-foreground transition-all duration-300 hover:tracking-[0.3em]"
          >
            Book a Ride
          </button>
        </div>

        <button onClick={() => setOpen((o) => !o)} data-testid="mobile-menu-toggle" className="text-white lg:hidden">
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            data-testid="mobile-menu"
            className="overflow-hidden border-t border-white/10 bg-black/90 backdrop-blur-2xl lg:hidden"
          >
            <div className="flex flex-col gap-1 px-6 py-6">
              {LINKS.map((l) => (
                <button key={l.href} onClick={() => go(l.href)} className="py-3 text-left font-serif text-2xl text-white">
                  {l.label}
                </button>
              ))}
              <button onClick={() => go("#book")} className="mt-4 bg-primary py-4 text-xs font-medium uppercase tracking-[0.2em] text-primary-foreground">
                Book a Ride
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
